import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    CrudFilters,
} from '@refinedev/core';
import { Table, Form, Alert } from 'antd';
import type { TablePaginationConfig } from 'antd';
import { TextField, useTable, EditButton } from '@refinedev/antd';
import {
    IApplication,
    IRecordFilterVariables,
} from 'interfaces/preliminary-application-wp.interface';
import WidthLimiter from '../../../components/width-limiter';
import buildSubmissionRow from '../../../utils/buildSubmissionRow';
import { CreateButton } from '../../../components/ui/create-button';
import { Heading } from '../../../components/ui/heading';
import { useDictionarySelectProps } from '../../../hooks/preliminary-application-wp/useDictionarySelectProps';
import { useProcedureSelectProps } from '../../../hooks/useProcedureSelectProps';
import type { FormInstance } from 'antd';
import styles from './list.module.scss';
import {
    FilterControls,
    type FilterDropdownOption,
} from '../../../components/applications/filter-controls';
import TablePagination from '../../../components/ui/table-pagination';

type ExtendedPaginationConfig = TablePaginationConfig & {
    pageSizeSelectorWidth?: number | string;
};

type IColumn = {
    id: string;
    reg_num: string;
    reg_date: string;
    medication_name: string;
    applicant_id: number;
    procedure_id: number;
    state_id: number;
    state?: {
        name?: string;
    };
};

export const PreliminaryApplicationOPCList: FC<
    IResourceComponentsProps
> = () => {
    const t = useTranslate();
    const {
        tableProps,
        tableQueryResult,
        searchFormProps,
        filters: activeFilters,
        setFilters,
        current,
        setCurrent,
        pageSize,
        setPageSize,
    } = useTable<IApplication, HttpError, IRecordFilterVariables>({
        filters: {
            defaultBehavior: 'replace',
        },
        onSearch: params => {
            const filters: CrudFilters = [];
            const {
                state_id,
                reg_num,
                preliminary_request_procedure_id,
                query,
            } = params;

            filters.push(
                {
                    field: 'query',
                    operator: 'eq',
                    value: query,
                },
                {
                    field: 'reg_num',
                    operator: 'eq',
                    value: reg_num,
                },
                {
                    field: 'state_id',
                    operator: 'eq',
                    value: state_id,
                },
                {
                    field: 'preliminary_request_procedure_id',
                    operator: 'eq',
                    value: preliminary_request_procedure_id,
                }
            );

            return filters;
        },
        resource: 'preliminary-request',
    });

    const { statusProps } = useDictionarySelectProps();
    const { CustomProcedureSelectProps, procedureQueryResult, procedureProps } =
        useProcedureSelectProps({
            filterMode: 'onlyPrice',
            optionLabel: 'name_for_applicant',
        });

    const formInstance = searchFormProps.form as FormInstance | undefined;
    const regNumValue = Form.useWatch('reg_num', formInstance);
    const stateValue = Form.useWatch('state_id', formInstance);
    const procedureValue = Form.useWatch(
        'preliminary_request_procedure_id',
        formInstance
    );

    const [procedureOptions, setProcedureOptions] = useState<
        FilterDropdownOption[] | null
    >((procedureProps?.options as FilterDropdownOption[] | undefined) ?? null);
    const [hasInitialProcedure, setHasInitialProcedure] = useState(false);
    const [filterHasProcedureKey, setFilterHasProcedureKey] = useState(false);

    const { pagination: rawPagination, ...restTableProps } = tableProps;
    const pagination =
        rawPagination === false
            ? undefined
            : (rawPagination as ExtendedPaginationConfig);

    const { options: stateOptions, onChange: originalStateOnChange } =
        statusProps ?? {};

    const {
        options: baseProcedureOptions,
        onChange: originalProcedureOnChange,
    } = procedureProps ?? {};

    useEffect(() => {
        const fetchData = async () => {
            const items = await CustomProcedureSelectProps();
            if (!items) return;

            setProcedureOptions(items as FilterDropdownOption[]);

            if (hasInitialProcedure) return;

            if (items.length > 0) {
                const firstItem = items[0];
                formInstance?.setFieldsValue({
                    preliminary_request_procedure_id: firstItem.value,
                });
                setFilters(prevFilters => {
                    const withoutProcedure = prevFilters.filter(filter => {
                        if (!('field' in filter)) return true;
                        return (
                            filter.field !== 'preliminary_request_procedure_id'
                        );
                    });

                    return [
                        ...withoutProcedure,
                        {
                            field: 'preliminary_request_procedure_id',
                            operator: 'eq',
                            value: firstItem.value,
                        },
                    ];
                });
                formInstance?.submit?.();
                setHasInitialProcedure(true);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureQueryResult.data]);

    useEffect(() => {
        const hasProcedure = activeFilters?.some(filter => {
            if (!('field' in filter)) return false;

            return (
                filter.field === 'preliminary_request_procedure_id' &&
                filter.value !== undefined &&
                filter.value !== null &&
                filter.value !== ''
            );
        });

        setFilterHasProcedureKey(Boolean(hasProcedure));
    }, [activeFilters]);

    const resolvedProcedureOptions = useMemo<FilterDropdownOption[]>(() => {
        if (procedureOptions) {
            return procedureOptions;
        }

        return (
            (baseProcedureOptions as FilterDropdownOption[] | undefined) ?? []
        );
    }, [baseProcedureOptions, procedureOptions]);

    const stateSelectOptions = useMemo<FilterDropdownOption[]>(() => {
        if (!stateOptions) return [];

        return stateOptions as FilterDropdownOption[];
    }, [stateOptions]);

    const procedureSelectOptions = useMemo<FilterDropdownOption[]>(() => {
        return resolvedProcedureOptions ?? [];
    }, [resolvedProcedureOptions]);

    const submitAfterChange = (handler?: (value: any, option: any) => void) => {
        return (value: any, option: any) => {
            handler?.(value, option);
            formInstance?.submit();
        };
    };

    const clearLabelText = t('applications.filters.clear', 'Очистити фільтр');
    const removeFilterByField = (field: string) => {
        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return filter.field !== field;
            })
        );
    };

    const handleStateSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const resolvedValue = nextValue ?? (option ? option.value : undefined);

        const shouldClear =
            resolvedValue === undefined ||
            resolvedValue === null ||
            (typeof resolvedValue === 'string' && resolvedValue.trim() === '');

        if (shouldClear) {
            formInstance.setFieldsValue({ state_id: undefined });
            originalStateOnChange?.(undefined as any, undefined as any);
            removeFilterByField('state_id');
            formInstance.submit();
            return;
        }

        const matchedOption =
            option ??
            stateSelectOptions.find(
                item => String(item.value) === String(resolvedValue)
            );

        formInstance.setFieldsValue({ state_id: resolvedValue });
        submitAfterChange(originalStateOnChange)(resolvedValue, matchedOption);
    };

    const handleProcedureSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const resolvedValue = nextValue ?? (option ? option.value : undefined);

        const shouldClear =
            resolvedValue === undefined ||
            resolvedValue === null ||
            (typeof resolvedValue === 'string' && resolvedValue.trim() === '');

        if (shouldClear) {
            formInstance.setFieldsValue({
                preliminary_request_procedure_id: undefined,
            });
            originalProcedureOnChange?.(undefined as any, undefined as any);
            removeFilterByField('preliminary_request_procedure_id');
            formInstance.submit();
            return;
        }

        const matchedOption =
            option ??
            procedureSelectOptions.find(
                item => String(item.value) === String(resolvedValue)
            );

        formInstance.setFieldsValue({
            preliminary_request_procedure_id: resolvedValue,
        });
        submitAfterChange(originalProcedureOnChange)(
            resolvedValue,
            matchedOption
        );
    };

    const clearedInitialFilters = useRef(false);
    const isInitialRegNumWatch = useRef(true);

    useEffect(() => {
        if (!formInstance || clearedInitialFilters.current) return;

        formInstance.setFieldsValue({
            state_id: undefined,
            reg_num: undefined,
        });

        clearedInitialFilters.current = true;
    }, [formInstance]);

    const isAllActive =
        !formInstance || (!stateValue && !procedureValue && !regNumValue);

    const handleResetFilters = () => {
        if (!formInstance) return;

        formInstance.setFieldsValue({
            state_id: undefined,
            preliminary_request_procedure_id: undefined,
            reg_num: undefined,
        });

        originalStateOnChange?.(undefined as any, undefined as any);
        originalProcedureOnChange?.(undefined as any, undefined as any);

        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return (
                    filter.field !== 'state_id' &&
                    filter.field !== 'preliminary_request_procedure_id' &&
                    filter.field !== 'reg_num'
                );
            })
        );
        formInstance.submit();
    };

    useEffect(() => {
        if (!formInstance) return;

        if (isInitialRegNumWatch.current) {
            isInitialRegNumWatch.current = false;
            return;
        }

        const handler = window.setTimeout(() => {
            formInstance.submit();
        }, 300);

        return () => window.clearTimeout(handler);
    }, [formInstance, regNumValue]);

    const currentPage = current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;
    const totalItems = tableQueryResult?.data?.total ?? pagination?.total ?? 0;
    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;

        setPageSize?.(value);
        setCurrent?.(1);
    };

    const pageSizeSelectorWidth = pagination?.pageSizeSelectorWidth;
    const resolvedPageSizeSelectorWidth =
        typeof pageSizeSelectorWidth === 'number'
            ? `${pageSizeSelectorWidth}px`
            : pageSizeSelectorWidth;

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.header}>
                <Heading level={1} align="center" className={styles.title}>
                    {t(
                        'preliminary-applications-wp.preliminary-applications',
                        'Заяви ГОВЦ'
                    )}
                </Heading>
                <FilterControls
                    formProps={searchFormProps}
                    hiddenFieldNames={[
                        'state_id',
                        'preliminary_request_procedure_id',
                    ]}
                    searchFieldName="reg_num"
                    searchPlaceholder={t(
                        'search.placeholder',
                        'Пошук заявки по номеру'
                    )}
                    filtersLabel={t('applications.titles.filters')}
                    clearFilterLabel={clearLabelText}
                    isAllActive={isAllActive}
                    onResetFilters={handleResetFilters}
                    dropdowns={[
                        {
                            placeholder: t(
                                'preliminary-applications.fields.state'
                            ),
                            value: stateValue,
                            options: stateSelectOptions,
                            onSelect: handleStateSelect,
                            clearLabel: clearLabelText,
                        },
                        {
                            placeholder: t(
                                'preliminary-applications.fields.procedure'
                            ),
                            value: procedureValue,
                            options: procedureSelectOptions,
                            onSelect: handleProcedureSelect,
                            clearLabel: clearLabelText,
                        },
                    ]}
                    extraActions={
                        <CreateButton
                            resource="preliminary-applications-wp"
                            label={t(
                                'preliminary-applications-wp.buttons.create_an_application_with_ev'
                            )}
                            className={styles.createButton}
                            variant="primary"
                        />
                    }
                />
            </header>
            <div className={styles.tableWrapper}>
                {filterHasProcedureKey ? (
                    <Table
                        {...restTableProps}
                        pagination={false}
                        rowKey="id"
                        className={styles.table}
                        tableLayout="fixed"
                    >
                        <Table.Column
                            dataIndex="medication_name"
                            key="medication_name"
                            title={t('applications.fields.medication_name')}
                            render={(value: string, row: IColumn) => (
                                <div className={styles.cellContent}>
                                    <WidthLimiter>
                                        <TextField value={value} />
                                    </WidthLimiter>
                                </div>
                            )}
                            sorter
                            width={300}
                            align="left"
                        />
                        <Table.Column
                            dataIndex="reg_num"
                            key="reg_num"
                            title={t('applications.fields.moz_data')}
                            render={(value, row: IColumn) => {
                                const val = value
                                    ? buildSubmissionRow(
                                          row,
                                          ['reg_num', 'reg_date'],
                                          ' від '
                                      )
                                    : '';

                                return val && <TextField value={val} />;
                            }}
                            sorter
                            width={300}
                            align="left"
                        />
                        <Table.Column
                            dataIndex="state_id"
                            key="state_id"
                            title={t('applications.fields.state')}
                            render={(_, row: IColumn) => (
                                <TextField value={row?.state?.name} />
                            )}
                            sorter
                            width={280}
                            align="left"
                        />
                        <Table.Column
                            title={t('table.actions')}
                            dataIndex="actions"
                            render={(_, record: any) => {
                                const itemId = record.id;

                                return (
                                    <EditButton
                                        hideText
                                        size="small"
                                        recordItemId={itemId}
                                    />
                                );
                            }}
                            align="center"
                            width={120}
                        />
                    </Table>
                ) : (
                    <Alert
                        message={t('labels.select_the_procedure_filter')}
                        type="warning"
                        showIcon
                        className={styles.selectProcedureAlert}
                    />
                )}
            </div>
            {filterHasProcedureKey && totalItems ? (
                <TablePagination
                    currentPage={currentPage}
                    pageSize={resolvedPageSize}
                    totalItems={totalItems}
                    pageSizeOptions={orderedPageSizeOptions}
                    onChangePage={handlePageChange}
                    onChangePageSize={handlePageSizeChange}
                    pageSizeSelectorWidth={resolvedPageSizeSelectorWidth}
                    previousLabel={t(
                        'applications.pagination.previous',
                        'Попередня сторінка'
                    )}
                    nextLabel={t(
                        'applications.pagination.next',
                        'Наступна сторінка'
                    )}
                    showDocumentsLabel={t(
                        'applications.pagination.show_documents',
                        'Показувати документи'
                    )}
                    rangeLabel={(start, end, total) =>
                        `${start}-${end} ${t(
                            'applications.pagination.of_total',
                            'з'
                        )} ${total}`
                    }
                />
            ) : null}
        </div>
    );
};
